import React from 'react';
import { Box, Card, CardContent, Skeleton } from '@mui/material';
import { motion } from 'framer-motion';

const SkeletonCard = ({ height = 200, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={16} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
        <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 2 }} />
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="text" width="60%" height={20} sx={{ mt: 1 }} />
        </Box>
      </CardContent>
    </Card>
  </motion.div>
);

const SkeletonTable = ({ rows = 5, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
          <Skeleton variant="text" width="30%" height={28} />
        </Box>
        {Array.from({ length: rows }).map((_, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="rectangular" width={40} height={40} sx={{ mr: 2, borderRadius: 1 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="70%" height={20} />
              <Skeleton variant="text" width="50%" height={16} sx={{ mt: 0.5 }} />
            </Box>
            <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 2 }} />
          </Box>
        ))}
      </CardContent>
    </Card>
  </motion.div>
);

const SkeletonStats = ({ delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Card sx={{ 
      borderRadius: 3, 
      boxShadow: 2,
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="text" width="60%" height={20} sx={{ mt: 1 }} />
          </Box>
          <Skeleton variant="circular" width={48} height={48} />
        </Box>
      </CardContent>
    </Card>
  </motion.div>
);

const SkeletonList = ({ items = 4, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
          <Skeleton variant="text" width="40%" height={24} />
        </Box>
        {Array.from({ length: items }).map((_, index) => (
          <Box key={index} sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            p: 2, 
            mb: 1.5,
            backgroundColor: '#f8fafc',
            borderRadius: 2,
            border: '1px solid #e2e8f0'
          }}>
            <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="text" width="60%" height={16} sx={{ mt: 0.5 }} />
            </Box>
            <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 2 }} />
          </Box>
        ))}
      </CardContent>
    </Card>
  </motion.div>
);

const SkeletonLoader = ({ 
  type = 'card', 
  count = 1, 
  height, 
  rows, 
  items,
  delay = 0 
}) => {
  const renderSkeleton = () => {
    switch (type) {
      case 'card':
        return Array.from({ length: count }).map((_, index) => (
          <SkeletonCard key={index} height={height} delay={delay + index * 0.1} />
        ));
      case 'table':
        return <SkeletonTable rows={rows} delay={delay} />;
      case 'stats':
        return <SkeletonStats delay={delay} />;
      case 'list':
        return <SkeletonList items={items} delay={delay} />;
      default:
        return <SkeletonCard height={height} delay={delay} />;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {renderSkeleton()}
    </Box>
  );
};

export default SkeletonLoader;
export { SkeletonCard, SkeletonTable, SkeletonStats, SkeletonList };
